import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  getVendorDirectoryDuplicateMatch,
  keepVendorDirectoryRecordsSeparate,
  deferVendorDirectoryDuplicateMatch,
  previewVendorDirectoryCandidateLink,
  executeVendorDirectoryCandidateLink,
  previewVendorDirectoryMerge,
  executeVendorDirectoryMerge,
} from "../../api/clientVendorDirectoryDuplicateApi";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.duplicates";
const SEPARATE_REASONS = [
  "different_owner",
  "different_location",
  "different_branch",
  "shared_phone_only",
  "similar_name_only",
  "other",
];

function bandBadgeClass(band) {
  if (band === "exact" || band === "very_likely") return "cvd-badge--published";
  if (band === "conflict") return "cvd-badge--suspended";
  if (band === "likely") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatScore(value) {
  if (value == null) return "—";
  return Number(value).toFixed(2);
}

function MetricCard({ label, value }) {
  return (
    <article className="cvd-metric-card">
      <span className="cvd-metric-card__label">{label}</span>
      <strong className="cvd-metric-card__value">{value ?? "—"}</strong>
    </article>
  );
}

function RecordPanel({ title, record, t }) {
  if (!record) return null;
  const isVendor = record.recordType === "vendor" || record.slug;

  return (
    <section className="cvd-panel cvd-duplicate-record">
      <h2>{title}</h2>
      <dl className="cvd-dl">
        <div>
          <dt>{t(`${COPY_PREFIX}.detail.name`)}</dt>
          <dd>{record.rawName || record.displayName || "—"}</dd>
        </div>
        {record.normalizedName ? (
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.normalized`)}</dt>
            <dd>{record.normalizedName}</dd>
          </div>
        ) : null}
        {(record.rawPhone || record.normalizedPhone) && (
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.phone`)}</dt>
            <dd>{record.rawPhone || record.normalizedPhone}</dd>
          </div>
        )}
        {(record.rawEmail || record.normalizedEmail) && (
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.email`)}</dt>
            <dd>{record.rawEmail || record.normalizedEmail}</dd>
          </div>
        )}
        {isVendor ? (
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.publicationStatus`)}</dt>
            <dd>{record.publicationStatus || "—"}</dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

export default function ClientVendorDirectoryDuplicateDetailPage() {
  const { clientViewId, matchId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const navigate = useNavigate();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [separateReason, setSeparateReason] = useState("similar_name_only");

  const basePath = `${serviciosBase}/duplicados`;
  const viewName = activeView?.name || "CasaMapa";

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVendorDirectoryDuplicateMatch(clientViewId, matchId);
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.detail.loadError`));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, matchId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleKeepSeparate = async () => {
    setBusy(true);
    setErrorMessage("");
    try {
      await keepVendorDirectoryRecordsSeparate(clientViewId, matchId, { reason: separateReason });
      await load();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDefer = async () => {
    setBusy(true);
    setErrorMessage("");
    try {
      await deferVendorDirectoryDuplicateMatch(clientViewId, matchId, {
        reason: "needs_more_information",
      });
      await load();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleLinkCandidate = async () => {
    setBusy(true);
    setErrorMessage("");
    try {
      const preview = await previewVendorDirectoryCandidateLink(clientViewId, matchId, {
        vendorId: data.match.vendorId,
      });
      await executeVendorDirectoryCandidateLink(clientViewId, matchId, {
        vendorId: data.match.vendorId,
        previewToken: preview.previewToken,
        acknowledgeConflicts: preview.preview.conflicts.length > 0,
      });
      if (data.match.candidateId) {
        navigate(`${serviciosBase}/candidatos/${data.match.candidateId}`);
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleMergeVendors = async () => {
    if (!window.confirm(t(`${COPY_PREFIX}.detail.mergeConfirm`))) return;
    setBusy(true);
    setErrorMessage("");
    try {
      const preview = await previewVendorDirectoryMerge(clientViewId, {
        sourceVendorId: data.match.vendorId,
        targetVendorId: data.match.vendorIdB,
      });
      await executeVendorDirectoryMerge(clientViewId, {
        sourceVendorId: data.match.vendorId,
        targetVendorId: data.match.vendorIdB,
        previewToken: preview.previewToken,
        reason: "duplicate_review_merge",
        acknowledgeConflicts: preview.preview.conflicts.length > 0,
      });
      navigate(`${serviciosBase}/proveedores/${data.match.vendorIdB}`);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content cvd-admin cvd-duplicates">
        <ClientPortalLoading message={t(`${COPY_PREFIX}.detail.loading`)} />
      </div>
    );
  }

  if (errorMessage && !data?.match) {
    return (
      <div className="page-content cvd-admin cvd-duplicates">
        <ClientPortalError message={errorMessage} onRetry={load} />
      </div>
    );
  }

  const { match, recordA, recordB } = data || {};

  return (
    <div className="page-content cvd-admin cvd-duplicates">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.detail.title`)}</h1>
          <p className="lede">{viewName}</p>
        </div>
        <div className="cvd-admin__actions">
          <Link className="cvd-btn cvd-btn--ghost" to={basePath}>
            ← {t(`${COPY_PREFIX}.backToList`)}
          </Link>
        </div>
      </div>

      {errorMessage ? (
        <div className="cvd-alert cvd-alert--warning" role="alert">
          {errorMessage}
        </div>
      ) : null}

      <div className="cvd-metric-grid">
        <MetricCard label={t(`${COPY_PREFIX}.detail.score`)} value={formatScore(match?.matchScore)} />
        <MetricCard
          label={t(`${COPY_PREFIX}.detail.confidence`)}
          value={t(`${COPY_PREFIX}.confidence.${match?.confidenceBand || "possible"}`, {}, match?.confidenceBand)}
        />
        <MetricCard
          label={t(`${COPY_PREFIX}.detail.type`)}
          value={t(`${COPY_PREFIX}.matchType.${match?.matchType}`, {}, match?.matchType)}
        />
      </div>

      <div className="cvd-duplicate-detail__confidence">
        <span className={`cvd-badge ${bandBadgeClass(match?.confidenceBand)}`}>
          {t(`${COPY_PREFIX}.confidence.${match?.confidenceBand || "possible"}`, {}, match?.confidenceBand)}
        </span>
      </div>

      <section className="cvd-panel">
        <h2>{t(`${COPY_PREFIX}.detail.explanation`)}</h2>
        {(match?.positiveSignals || []).length > 0 ? (
          <ul className="cvd-duplicate-signals cvd-duplicate-signals--positive">
            {match.positiveSignals.map((signal, index) => (
              <li key={`pos-${index}`}>{signal}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">—</p>
        )}
        {(match?.negativeSignals || []).length > 0 ? (
          <>
            <h3>{t(`${COPY_PREFIX}.detail.conflicts`)}</h3>
            <ul className="cvd-duplicate-signals cvd-duplicate-signals--negative">
              {match.negativeSignals.map((signal, index) => (
                <li key={`neg-${index}`}>{signal}</li>
              ))}
            </ul>
          </>
        ) : null}
        <p className="cvd-duplicate-detail__meta muted">
          {t(`${COPY_PREFIX}.detail.model`)}: {match?.scoringModelVersion} · {t(`${COPY_PREFIX}.detail.normalization`)}:{" "}
          {match?.normalizationVersion}
        </p>
      </section>

      <div className="cvd-duplicate-compare">
        <RecordPanel title={t(`${COPY_PREFIX}.detail.recordA`)} record={recordA} t={t} />
        <RecordPanel title={t(`${COPY_PREFIX}.detail.recordB`)} record={recordB} t={t} />
      </div>

      <section className="cvd-panel cvd-duplicate-decisions">
        <h2>{t(`${COPY_PREFIX}.detail.decisions`)}</h2>
        <div className="cvd-duplicate-decisions__actions">
          {match?.matchType === "candidate_vendor" ? (
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.DUPLICATES_RESOLVE}>
              <button type="button" className="cvd-btn cvd-btn--primary" onClick={handleLinkCandidate} disabled={busy}>
                {t(`${COPY_PREFIX}.detail.linkCandidate`)}
              </button>
            </VendorDirectoryPermissionGate>
          ) : null}
          {match?.matchType === "vendor_vendor" ? (
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.DUPLICATES_MERGE}>
              <button type="button" className="cvd-btn cvd-btn--primary" onClick={handleMergeVendors} disabled={busy}>
                {t(`${COPY_PREFIX}.detail.mergeVendors`)}
              </button>
            </VendorDirectoryPermissionGate>
          ) : null}
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.DUPLICATES_KEEP_SEPARATE}>
            <div className="cvd-duplicate-decisions__separate">
              <label className="cvd-field" htmlFor="separate-reason">
                {t(`${COPY_PREFIX}.detail.separateReasonLabel`)}
                <select
                  id="separate-reason"
                  value={separateReason}
                  onChange={(event) => setSeparateReason(event.target.value)}
                >
                  {SEPARATE_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {t(`${COPY_PREFIX}.separateReason.${reason}`)}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="cvd-btn cvd-btn--secondary" onClick={handleKeepSeparate} disabled={busy}>
                {t(`${COPY_PREFIX}.detail.keepSeparate`)}
              </button>
            </div>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.DUPLICATES_RESOLVE}>
            <button type="button" className="cvd-btn cvd-btn--ghost" onClick={handleDefer} disabled={busy}>
              {t(`${COPY_PREFIX}.detail.defer`)}
            </button>
          </VendorDirectoryPermissionGate>
        </div>
      </section>
    </div>
  );
}
